import { useMemo } from 'react';
import { onlyDateFromServer, useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { toNumber, toString } from 'lodash';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { API_URL, IOcrResultDto } from '@/dtos';
import { OcrResult } from '@/modules/UseMyMoney/UseMyMoney.types';
import { formatOcrAccountOrder, getAccountOrdersArraysWithUniqServices, getAvailableAccounts } from '@/modules/UseMyMoney/utils';

interface Props {
  documentId?: string;
  employeeId?: string;
  dependentId?: string;
  serviceOn?: string;
  amount?: string;
  serviceId?: string;
  providerId?: string;
  overrideRunout?: boolean;
}

const useGetOcrOptionsQuery = ({
  documentId,
  employeeId,
  dependentId,
  serviceOn,
  amount,
  serviceId,
  providerId,
  overrideRunout,
}: Props) => {
  const { data, isLoading } = useQuery(
    [API_URL.DOCUMENTS_OCR_OPTIONS, employeeId, dependentId, serviceOn, amount, serviceId, providerId, overrideRunout],
    () => api.get<IOcrResultDto>(API_URL.DOCUMENTS_OCR_OPTIONS, {
      params: {
        ...employeeId ? { employee_id: employeeId } : {},
        ...dependentId ? { dependent_id: dependentId } : {},
        ...serviceOn ? { service_on: dayjs(serviceOn).format(DEFAULT_DATE_FORMAT) } : {},
        ...amount ? { amount: toNumber(amount) } : {},
        ...serviceId ? { service_id: serviceId } : {},
        ...overrideRunout ? { override_runout: overrideRunout } : {},
      },
    }),
    {
      enabled: !documentId,
    }
  );

  const ocrOptions = useMemo((): OcrResult | undefined => {
    if (!data?.data) {
      return;
    }

    const ocr = data?.data;
    const accountOrders = ocr?.account_orders?.flat()?.map(formatOcrAccountOrder);
    const availableAccounts = getAvailableAccounts(accountOrders);
    const accountOrdersArraysWithUniqServices = getAccountOrdersArraysWithUniqServices(ocr?.account_orders);

    return {
      providers: ocr?.providers?.map((provider) => ({
        id: toString(provider?.id),
        name: provider?.name,
        isSuggested: false,
        address: {
          city: provider?.address?.city,
          line1: provider?.address?.line1,
          line2: provider?.address?.line2,
          zipcode: provider?.address?.zip,
          state: {
            id: toString(provider?.address?.state?.id),
            code: provider?.address?.state?.code,
            name: provider?.address?.state?.name,
          },
        },
      })),
      addressOptions: ocr?.address_options?.map((address) => ({
        line1: address?.line1,
        line2: address?.line2,
        city: address?.city,
        state: {
          id: toString(address?.state?.id),
          code: address?.state?.code,
          name: address?.state?.name,
        },
        zipcode: address?.zip,
      })),
      serviceForOptions: ocr?.service_for_options?.map((serviceFor) => ({
        id: serviceFor?.dependent_id
          ? toString(serviceFor?.dependent_id)
          : toString(serviceFor?.employee_id),
        employeeId: toString(serviceFor?.employee_id),
        dependentId: toString(serviceFor?.dependent_id),
        fullName: serviceFor?.full_name,
        relationshipType: serviceFor?.relationship_type,
        birthOn: serviceFor?.birth_on
          ? onlyDateFromServer(`${serviceFor?.birth_on}`)
          : undefined,
        receiptName: serviceFor?.receipt_name,
        isSuggested: false,
      })),
      serviceOptions: ocr?.service_options?.map((service) => ({
        serviceId: toString(service?.service_id),
        serviceName: service?.service_name,
        categoryType: service?.category_type,
        isSuggested: false,
      })),
      amountOptions: ocr?.amount_options?.map((amount) => ({
        amount: amount?.amount !== undefined
          ? toString(toNumber(amount?.amount).toFixed(2)) 
          : undefined,
        isSuggested: false,
      })),
      serviceDateOptions: ocr?.service_date_options?.map((serviceDate) => ({
        date: serviceDate?.date
          ? dayjs(onlyDateFromServer(`${serviceDate?.date}`)).format(DEFAULT_DATE_FORMAT)
          : undefined,
        isSuggested: false,
      })),
      accountOrders,
      availableAccounts,
      accountOrdersArraysWithUniqServices,
      isReceiptRequired: ocr?.is_receipt_required,
    };
  }, [data]);

  return {
    ocrOptions,
    isOcrOptionsLoading: isLoading,
  };
};

export default useGetOcrOptionsQuery;
